import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { noticeActions } from "../../redux/noticeSlice";
import { fetchResidences } from "../../redux/userSlice";
import {
  Skeleton,
  Button,
  Modal,
  Pagination,
  message,
  Form,
  Input,
  Select,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { convertToNepaliDate } from "../../components/FormatDate";

const NoticeList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allNotices, loading, currentNotice } = useSelector((state) => state.notices);
  const { residences } = useSelector((state) => state.user);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [viewNotice, setViewNotice] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [noticeType, setNoticeType] = useState(null);
  const [form] = Form.useForm();

  // Fetch all notices on mount
  useEffect(() => {
    dispatch(noticeActions.getAllNotices());
  }, [dispatch]);

  // Fetch residences when editing a private notice
  useEffect(() => {
    if (isEditModalVisible && noticeType === "private") {
      dispatch(fetchResidences());
    }
  }, [dispatch, isEditModalVisible, noticeType]);

  // Populate form when currentNotice is loaded
  useEffect(() => {
    if (currentNotice && isEditModalVisible) {
      setSelectedNotice(currentNotice);
      setNoticeType(currentNotice.notice_type);

      // Map houses to user IDs for Select
      const selectedHouseIds = currentNotice.houses?.map((house) => house.id) || [];

      form.setFieldsValue({
        title: currentNotice.title,
        notice_type:currentNotice.notice_type,
        notice_body: currentNotice.notice_body,
        houses: selectedHouseIds,
         // This fills the residences
      });
    }
  }, [currentNotice, isEditModalVisible, form]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSubmitEdit = async (values) => {
    try {
      const id = selectedNotice?.id;
      if (!id) throw new Error("Notice ID is missing.");

      const updateData = { ...values,
        notice_type: selectedNotice.notice_type,
       };

      if (selectedNotice.notice_type === "private") {
        updateData.houses = values.houses || [];
        await dispatch(
          noticeActions.updatePrivateNotice({ id, noticeData: updateData })
        ).unwrap();
      } else {
        await dispatch(
          noticeActions.updatePublicNotice({ id, noticeData: updateData })
        ).unwrap();
      }

      message.success("Notice updated successfully");
      setIsEditModalVisible(false);
      form.resetFields();
      dispatch(noticeActions.getAllNotices());
      navigate("/dashboard/noticelist");
    } catch (error) {
      console.error("Error updating notice:", error);
      message.error(error.message || "Failed to update notice");
    }
  };

  const handleView = (notice) => {
    setViewNotice(notice);
  };

  const handleEdit = async (notice) => {
    if (!notice) return;

    try {
      setIsEditModalVisible(true);
      setNoticeType(notice.notice_type);

      // Fetch fresh notice data
      await dispatch(noticeActions.getNoticeById(notice.id)).unwrap();
    } catch (error) {
      console.error("Error fetching notice:", error);
      message.error("Failed to load notice data");
      setIsEditModalVisible(false);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this notice?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await dispatch(noticeActions.deleteNotice(id)).unwrap();
          message.success("Notice deleted successfully");
          dispatch(noticeActions.getAllNotices());
        } catch (error) {
          console.error(error);
          message.error("Failed to delete notice");
        }
      },
    });
  };

  const paginatedNotices = allNotices?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold">List Of Notices</h1>
          <Link to="/dashboard/notices">
            <Button
              type="default"
              className="bg-green-800 text-white hover:bg-green-600"
              icon={<PlusOutlined />}
            >
              Add New Notice
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="p-12">
            <Skeleton active />
          </div>
        ) : (
          <div className="bg-white p-4 rounded-lg overflow-x-auto">
            <table className="min-w-full border-2 border-black">
              <thead>
                <tr className="text-left bg-gray-100">
                  <th className="py-2 px-4 border-r border-b-2 border-black">S.N</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Title</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Date</th>
                  <th className="py-2 px-4 border-r border-b-2 border-black">Type</th>
                  <th className="py-2 px-4 border-b-2 border-black">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedNotices?.length > 0 ? (
                  paginatedNotices.map((notice, index) => (
                    <tr key={notice.id} className="hover:bg-gray-50 border-b border-black">
                      <td className="py-2 px-4 border-r border-black">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-2 px-4 border-r border-black">{notice.title}</td>
                      <td className="py-2 px-4 border-r border-black">
                        {convertToNepaliDate(new Date(notice.created_at))}
                      </td>
                      <td className="py-2 px-4 border-r border-black">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            notice.notice_type === "private"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {notice.notice_type}
                        </span>
                      </td>
                      <td className="py-2 px-4 flex gap-2">
                        <Button
                          type="default"
                          size="small"
                          className="bg-blue-700 text-white hover:bg-blue-800"
                          onClick={() => handleEdit(notice)}
                          icon={<EditOutlined />}
                        />
                        <Button
                          danger
                          size="small"
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => handleDelete(notice.id)}
                          icon={<DeleteOutlined />}
                        />
                        <Button
                          type="default"
                          size="small"
                          className="bg-gray-600 text-white hover:bg-gray-700"
                          onClick={() => handleView(notice)}
                          icon={<EyeOutlined />}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">
                      No notices found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={allNotices?.length || 0}
          onChange={handlePageChange}
          className="mt-6 text-center"
          showSizeChanger={false}
        />

        {/* View Notice Modal */}
        <Modal
          title={viewNotice?.title}
          open={!!viewNotice}
          onCancel={() => setViewNotice(null)}
          footer={null}
          width={600}
        >
          <div className="mt-4">
            <p className="text-gray-700 whitespace-pre-wrap">{viewNotice?.notice_body}</p>
            {viewNotice?.notice_type === "private" && viewNotice?.houses?.length > 0 && (
              <div className="mt-4">
                <strong>Sent to:</strong>
                <ul className="list-disc pl-5 mt-1">
                  {viewNotice.houses.map((house) => (
                    <li key={house.id}>{house.house_number}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>

        {/* Edit Notice Modal */}
        <Modal
          title="Edit Notice"
          open={isEditModalVisible}
          onCancel={() => {
            setIsEditModalVisible(false);
            form.resetFields();
            setSelectedNotice(null);
            setNoticeType(null);
          }}
          footer={null}
          width={600}
        >
          {loading ? (
            <Skeleton active />
          ) : (
            <Form form={form} onFinish={handleSubmitEdit} layout="vertical">
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: "Please enter the title" }]}
              >
                <Input placeholder="Enter notice title" />
              </Form.Item>

              <Form.Item
                name="notice_body"
                label="Notice Body"
                rules={[{ required: true, message: "Please enter the notice body" }]}
              >
                <Input.TextArea rows={5} placeholder="Enter notice description" />
              </Form.Item>

              {noticeType === "private" && (
                <Form.Item
                  name="houses"
                  label="Select Residences (Houses)"
                  rules={[{ required: true, message: "Please select at least one residence" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Search and select houses..."
                    loading={!residences?.data}
                    optionFilterProp="label"
                    showSearch
                    options={residences?.data?.map((res) => ({
                      value: res.id,
                      label: res.name || res.house_number || `House ${res.id}`,
                    }))}
                  />
                </Form.Item>
              )}

              <div className="flex gap-2 mt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="bg-green-700 hover:bg-green-800 flex-1"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => {
                    setIsEditModalVisible(false);
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default NoticeList;